
























































































import { Component } from 'vue-property-decorator';
import GiganautVue from '@/components/Shared/GiganautVue';
import Alert from '@/components/Shared/Alert.vue';
import OpportunityStatus from '@/components/Opportunities/Display/OpportunityStatus.vue';
import OpportunityCreate from '@/components/Opportunities/OpportunityCreate.vue';
import OpportunityListSearchCriteria from '@/components/Opportunities/OpportunityListSearchCriteria.vue';
import OpportunityStageEnum from '@/models/Opportunities/OpportunityStage';
import OpportunityDto from '@/models/Opportunities/OpportunityDto';
import StatusDisplayLocation from '@/models/Shared/StatusDisplayLocation';
import { OpportunitiesListModule } from '@/store/modules/opportunitiesList';
import { AppModule } from '@/store/modules/app';
import PageNames from '@/router/PageNames';

@Component({
    name: 'AgencyOpportunities',
    components: { Alert, OpportunityStatus, OpportunityCreate, OpportunityListSearchCriteria },
})
export default class extends GiganautVue {
    OpportunityStage = OpportunityStageEnum;
    StatusDisplayLocation = StatusDisplayLocation;

    search = '';
    stage: OpportunityStageEnum = OpportunityStageEnum.Undefined;
    // Dormancy (last-activity) sub-filter. Only applied on an in-flight pipeline
    // stage (New/Qualified/Proposing/Negotiating); defaults to Active.
    dormancy = 'active';
    selectedRows: OpportunityDto[] = [];

    // Mirrors Jobs.vue columns exactly: Ref / Branch / Name (=Title) / Client (=Company) / Venue (=Location) / Status / Actions.
    // Stage is filtered via the button row; contact and per-row metadata are
    // intentionally not in the grid to keep it scannable.
    headers = [
        { text: 'Ref', value: 'sequenceNumber', width: 80 },
        { text: 'Branch', value: 'branchName' },
        { text: 'Name', value: 'name' },
        { text: 'Client', value: 'clientName' },
        { text: 'Venue', value: 'venueName' },
        { text: 'Status', value: 'status', width: 90 },
        { text: '', value: 'actions', sortable: false, width: 60 },
    ];

    get loading(): boolean {
        return OpportunitiesListModule.loading;
    }

    get opportunities(): OpportunityDto[] {
        return OpportunitiesListModule.opportunities;
    }

    // Intermediate stage buttons are hidden when the agency has disabled the
    // stage via AgencySettings (surfaced on currentAgencyContext).
    get agencyContext() { return AppModule.currentAgencyContext; }
    get showStageQualified(): boolean { return !this.agencyContext || this.agencyContext.showOpportunityStageQualified !== false; }
    get showStageProposing(): boolean { return !this.agencyContext || this.agencyContext.showOpportunityStageProposing !== false; }
    get showStageNegotiating(): boolean { return !this.agencyContext || this.agencyContext.showOpportunityStageNegotiating !== false; }


    get atNew() { return this.opportunities.filter(o => o.stage === OpportunityStageEnum.New); }
    get atQualified() { return this.opportunities.filter(o => o.stage === OpportunityStageEnum.Qualified); }
    get atProposing() { return this.opportunities.filter(o => o.stage === OpportunityStageEnum.Proposing); }
    get atNegotiating() { return this.opportunities.filter(o => o.stage === OpportunityStageEnum.Negotiating); }
    get atWon() { return this.opportunities.filter(o => o.stage === OpportunityStageEnum.Won); }
    get atLost() { return this.opportunities.filter(o => o.stage === OpportunityStageEnum.Lost); }

    // The dormancy filter is only usable on an in-flight pipeline stage.
    get isPipelineStage(): boolean {
        return this.stage === OpportunityStageEnum.New
            || this.stage === OpportunityStageEnum.Qualified
            || this.stage === OpportunityStageEnum.Proposing
            || this.stage === OpportunityStageEnum.Negotiating;
    }

    // Opportunities matching just the active stage button (before dormancy).
    get stageFilteredItems(): OpportunityDto[] {
        if (this.stage === OpportunityStageEnum.Undefined) return this.opportunities;
        return this.opportunities.filter(o => o.stage === this.stage);
    }

    get visibleOpportunities(): OpportunityDto[] {
        if (!this.isPipelineStage) return this.stageFilteredItems;
        return this.stageFilteredItems.filter(o => (o.dormancyStatus || 'stale') === this.dormancy);
    }

    dormancyCount(bucket: string): number {
        return this.stageFilteredItems.filter(o => (o.dormancyStatus || 'stale') === bucket).length;
    }

    dormancyColor(bucket: string): string {
        switch (bucket) {
            case 'active': return 'green';
            case 'cooling': return 'amber darken-2';
            case 'dormant': return 'orange';
            case 'stale': return 'red';
            default: return 'grey';
        }
    }

    setDormancy(bucket: string) {
        if (!this.isPipelineStage) return;
        this.dormancy = bucket;
    }

    // Per-stage colour. Active button = filled; inactive = outlined in same colour.
    stageColor(s: OpportunityStageEnum): string {
        switch (s) {
            case OpportunityStageEnum.Undefined: return 'primary';
            case OpportunityStageEnum.New: return 'grey';
            case OpportunityStageEnum.Qualified:
            case OpportunityStageEnum.Proposing:
            case OpportunityStageEnum.Negotiating: return 'orange';
            case OpportunityStageEnum.Won: return 'green';
            case OpportunityStageEnum.Lost: return 'red';
            default: return 'primary';
        }
    }

    setStage(stage: OpportunityStageEnum) {
        this.stage = stage;
    }

    navigateToOpportunity(id: string) {
        this.$router.push({ name: PageNames.AgencyOpportunityDetails, params: { opportunityId: id } });
    }

    branchName(opp: OpportunityDto): string {
        return opp.branch && opp.branch.brandName ? opp.branch.brandName : '';
    }

    // Client / Venue cells: prefer the linked record's brandName, fall back to
    // the prospect snapshot.
    clientDisplay(opp: OpportunityDto): string {
        if (opp.company && opp.company.brandName) return opp.company.brandName;
        if (opp.prospectCompanyName) return opp.prospectCompanyName;
        return '';
    }

    venueDisplay(opp: OpportunityDto): string {
        if (opp.location && opp.location.brandName) return opp.location.brandName;
        if (opp.prospectLocationName) return opp.prospectLocationName;
        return '';
    }

    async beforeMount() {
        await OpportunitiesListModule.fetch();
    }
}
